/* forager_down — the walk you see more than any other pixel in the game.
 *
 * It was two frames before this file existed: the authored pose and
 * `bobbed()` of it, alternating. Both are still here, exactly as art.ts drew
 * them, at the two phases that carry the stride —
 *
 *   0  tall, feet together at 3-5 / 10-12, weight centred  (the authored pose)
 *   4  a row lower, shoulders spread, feet splayed to 2-4 / 11-13  (`bobbed`)
 *
 * — and the other six are drawn between them. The first attempt at those six
 * measured 46/11/88/11/96/13/13/40 changed pixels per step: two transitions
 * carried the whole pose change and four were boot tweaks of eleven and
 * thirteen pixels. That is four poses (0, 1-2, 3-4, 5-7) with padding, and it
 * failed in three specific ways this set fixes.
 *
 * THE BODY SLID; NOW THE HEAD LEANS. Head and torso used to move together as
 * one rigid block, jumping two columns across the cycle — a body sliding
 * sideways, not walking. The torso core now holds still at columns 2-13 in
 * every frame and only the hood and face (rows 1-6) shift one column, over
 * whichever boot is carrying: left on 1-3, right on 5-7, level on 0 and 4
 * where both boots are down and the weight passes through the middle.
 *
 * THE BOB WAS A SQUARE WAVE; NOW IT FOLDS IN TWO BEATS. The sprite used to
 * drop a whole row in one step (88px) and come back up in one step (96px),
 * with the frames either side of each being near-copies. A body does not
 * arrive all at once, so the fall is split: at 2 the head and shoulders come
 * down and the hips hold, so the torso loses a row and bulges either side of
 * the belt; at 3 the hips and hem follow and the legs fold under. The rise
 * undoes it in the same two beats the other way round — hips and hem at 6,
 * where the boot row comes back, and the head at 7. Frame 5 is the one that
 * spends the bottom of the drop transferring the weight rather than moving
 * height. Nothing translates as a slab.
 *
 * BOTH LEGS WORK. The left boot used to sit at column 3 for the entire cycle
 * while the right did all eight frames of travelling — a limp. Each boot now
 * gets a beat off the ground: the right heel breaks at 1 and the whole boot
 * is airborne at 2 (no toes on row 15) before it strikes wide at 3-4; the left
 * heel breaks at 5 and it is airborne at 6 before it plants at 7. Frame 7
 * leaves the right heel up and one column out, which is the last pixel of
 * travel frame 0 needs — that is what makes 7 lead into 0 instead of snapping.
 *
 * The satchel rides the hip on every frame but the two the body moves fastest
 * through: it is left hanging a row high at 3, where the hips dropped and the
 * bag had not, and a row low at 6, where they came back up without it. The
 * scarf end darkens to K on the side it has been thrown to.
 *
 * Mass is held by the squash, not by luck. Compressing the torso costs a
 * twelve-pixel row, so every frame that is down (2 through 6) bulges one column
 * out above and below the belt to pay for it, and the hood narrows into the
 * shoulders as the drop deepens — one row at 3 and 5, two at 4, which is the
 * `bobbed` pose's own doing. Ink runs 154/154/148/151/150/151/146/154, a 5.3%
 * swing, and the lowest lit row is 15 in all eight frames: the body rises and
 * falls, the feet do not. Steps are 41/66/31/45/41/29/66/45 — uneven, as a gait
 * is, with the two head-drops the largest and nothing near a copy.
 */

export const forager_down_0 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcsssscCo...',
  '...oCsossosCo...',
  '...oCcsssscCo...',
  '..oCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '..oCccccccccCoo.',
  '..oCcbbbbbbcCoMo',
  '..oCccccccccCoMo',
  '..oCccccccccCoo.',
  '..ooccccccccoo..',
  '...obbo..obbo...',
  '...bbb....bbb...',
];

export const forager_down_1 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCccccccCo....',
  '..oCcsssscCo....',
  '..oCsossosCo....',
  '..oCcsssscCo....',
  '..oKkkkkkkkkCo..',
  '..oCccccccccCo..',
  '..oCccccccccCoo.',
  '..oCcbbbbbbcCoMo',
  '..oCccccccccCoMo',
  '..oCccccccccCoo.',
  '.oooccccccccoo..',
  '...obbo..obbo...',
  '...bbb.....bb...',
];

export const forager_down_2 = [
  '................',
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCccccccCo....',
  '..oCcsssscCo....',
  '..oCsossosCo....',
  '..oCcsssscCo....',
  '.ooKkkkkkkkkCoo.',
  '.ooCccccccccCooo',
  '.ooCcbbbbbbcCoMo',
  '.ooCccccccccCoMo',
  '.ooCccccccccCooo',
  '.oooccccccccoo..',
  '...obbo...obbo..',
  '...bbb..........',
];

export const forager_down_3 = [
  '................',
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '...CccccccC.....',
  '..oCcsssscCo....',
  '..oCsossosCo....',
  '..oCcsssscCo....',
  '.ooKkkkkkkkkCoo.',
  '.ooCccccccccCooo',
  '.ooCccccccccCoMo',
  '.ooCcbbbbbbcCoMo',
  '.ooCccccccccCooo',
  '..oCccccccccCo..',
  '..ooccccccccoo..',
  '..bbb.......bb..',
];

export const forager_down_4 = [
  '................',
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '....CccccccC....',
  '....CcsssscC....',
  '..ooCsossosCoo..',
  '..ooCcsssscCoo..',
  '.ooCkkkkkkkkCoo.',
  '.ooCccccccccCoo.',
  '.ooCccccccccCooo',
  '...CcbbbbbbcCoM.',
  '..oCccccccccCoMo',
  '..oCccccccccCoo.',
  '..ooccccccccoo..',
  '..bbb......bbb..',
];

export const forager_down_5 = [
  '................',
  '................',
  '.......oooo.....',
  '......oCCCCo....',
  '.....CccccccC...',
  '....oCcsssscCo..',
  '....oCsossosCo..',
  '....oCcsssscCo..',
  '.ooCkkkkkkkkKoo.',
  '.ooCccccccccCoo.',
  '.ooCccccccccCooo',
  '.ooCcbbbbbbcCoMo',
  '.ooCccccccccCoMo',
  '..oCccccccccCoo.',
  '..ooccccccccoo..',
  '..bb.......bbb..',
];

export const forager_down_6 = [
  '................',
  '................',
  '.......oooo.....',
  '......oCCCCo....',
  '....oCccccccCo..',
  '....oCcsssscCo..',
  '....oCsossosCo..',
  '....oCcsssscCo..',
  '.ooCkkkkkkkkKoo.',
  '.ooCccccccccCoo.',
  '.ooCcbbbbbbcCoo.',
  '.ooCccccccccCoMo',
  '.ooCccccccccCoMo',
  '..ooccccccccooo.',
  '...obbo...obbo..',
  '...........bbb..',
];

export const forager_down_7 = [
  '................',
  '.......oooo.....',
  '......oCCCCo....',
  '....oCccccccCo..',
  '....oCcsssscCo..',
  '....oCsossosCo..',
  '....oCcsssscCo..',
  '..oCkkkkkkkkKo..',
  '..oCccccccccCo..',
  '..oCccccccccCoo.',
  '..oCcbbbbbbcCoMo',
  '..oCccccccccCoMo',
  '..oCccccccccCoo.',
  '..ooccccccccooo.',
  '...obbo...obbo..',
  '...bbb.....bb...',
];
